import React, { useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { FaEye, FaRegTrashAlt } from 'react-icons/fa';
import { RenderIf } from '../utils/RenderIf';
import { checkUserPermission } from '../utils/CheckUserPermitions';
import defaultImg from '../../assets/img/default.png';

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .tbl-
───────────────────────────────────────────────────────── */
const TBL_CSS = `
@keyframes tbl-up   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
@keyframes tbl-fade { from{opacity:0} to{opacity:1} }

/* ── wrapper ── */
.tbl-wrap {
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  overflow: hidden;
  animation: tbl-up .28s ease both;
}

/* ── toolbar ── */
.tbl-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .75rem;
  padding: .9rem 1.15rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}
.tbl-title {
  font-size: .88rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  display: flex; align-items: center; gap: .4rem;
}
.tbl-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 18px; border-radius: 5px;
  background: rgba(27,58,107,.09); color: #1B3A6B;
  font-size: .65rem; font-weight: 700; padding: 0 .35rem;
}

/* ── search ── */
.tbl-search {
  display: flex; align-items: center; gap: .45rem;
  padding: .4rem .8rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 999px;
  background: var(--bg-2, rgba(0,0,0,.03));
  transition: border-color .14s, box-shadow .14s;
}
.tbl-search:focus-within {
  border-color: #1B3A6B;
  box-shadow: 0 0 0 3px rgba(27,58,107,.10);
  background: var(--bg, #fff);
}
.tbl-search svg { color: var(--text-3, #B4B4B4); flex-shrink: 0; }
.tbl-search input {
  border: none; background: transparent; outline: none;
  font-size: .81rem; color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  width: 170px; min-width: 0;
}
.tbl-search input::placeholder { color: var(--text-3, #B4B4B4); }
@media(max-width:480px){ .tbl-search input{ width:130px } }

/* ── table (desktop) ── */
.tbl-table-wrap {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,.08) transparent;
}
.tbl-table-wrap::-webkit-scrollbar { height: 4px; }
.tbl-table-wrap::-webkit-scrollbar-thumb { background: rgba(0,0,0,.08); border-radius: 2px; }

.tbl-table {
  width: 100%; border-collapse: collapse;
  font-family: var(--font-sans, -apple-system, sans-serif);
}
.tbl-table thead tr { border-bottom: 1.5px solid var(--border, rgba(0,0,0,.07)); }
.tbl-table th {
  padding: .6rem 1rem;
  font-size: .67rem; font-weight: 700;
  letter-spacing: .07em; text-transform: uppercase;
  color: var(--text-3, #B4B4B4);
  text-align: left; white-space: nowrap;
  background: var(--bg-2, rgba(0,0,0,.025));
}
.tbl-table td {
  padding: .72rem 1rem;
  font-size: .81rem; color: var(--text-2, #6B6B6B);
  border-bottom: 1px solid var(--border, rgba(0,0,0,.06));
  vertical-align: middle;
}
.tbl-table tbody tr { transition: background .12s; }
.tbl-table tbody tr:hover { background: var(--bg-2, rgba(0,0,0,.025)); }
.tbl-table tbody tr:last-child td { border-bottom: none; }

/* thumbnails */
.tbl-thumb {
  width: 38px; height: 38px; border-radius: 8px;
  object-fit: cover;
  border: 1px solid var(--border, rgba(0,0,0,.07));
  background: var(--bg-2, rgba(0,0,0,.04));
}

/* badges */
.tbl-badge {
  display: inline-flex; align-items: center; gap: .25rem;
  padding: .18rem .55rem; border-radius: 999px;
  font-size: .67rem; font-weight: 700; white-space: nowrap;
}
.tbl-badge-success { background: rgba(34,197,94,.1);  color: #15803D; }
.tbl-badge-danger  { background: rgba(239,68,68,.08); color: #B91C1C; }
.tbl-badge-warn    { background: rgba(245,158,11,.1); color: #92400E; }
[data-theme="dark"] .tbl-badge-success { background: rgba(34,197,94,.15);  color: #4ADE80; }
[data-theme="dark"] .tbl-badge-danger  { background: rgba(239,68,68,.12);  color: #FCA5A5; }

/* action buttons */
.tbl-action-row { display: flex; align-items: center; gap: .25rem; }
.tbl-btn {
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: transparent;
  color: var(--text-3, #B4B4B4);
  display: grid; place-items: center;
  cursor: pointer; transition: all .14s;
  font-size: .88rem;
}
.tbl-btn:hover          { background: rgba(27,58,107,.09); color: #1B3A6B; }
.tbl-btn.tbl-btn-danger:hover { background: rgba(239,68,68,.08); color: #EF4444; }

/* switch override */
.tbl-switch .form-check-input:checked { background-color: #1B3A6B; border-color: #1B3A6B; }
.tbl-switch .form-check-input:focus   { box-shadow: 0 0 0 3px rgba(27,58,107,.15); }

/* ── empty ── */
.tbl-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 3rem 1rem; text-align: center; animation: tbl-up .3s ease both;
}
.tbl-empty-icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: var(--bg-2, rgba(0,0,0,.04));
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  display: grid; place-items: center;
  font-size: 1.4rem; margin-bottom: .65rem;
}
.tbl-empty-title { font-size: .86rem; font-weight: 600; color: var(--text, #0A0A0A); margin: 0 0 .2rem; }
.tbl-empty-sub   { font-size: .76rem; color: var(--text-3, #B4B4B4); margin: 0; }

/* ── mobile cards grid ── */
.tbl-cards { display: none; flex-direction: column; gap: .45rem; padding: .65rem; }

.tbl-card {
  display: flex; align-items: center; gap: .7rem;
  padding: .8rem .9rem;
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 12px; background: var(--bg, #fff);
  transition: box-shadow .14s, border-color .14s;
  animation: tbl-up .2s ease both;
}
.tbl-card:hover { box-shadow: 0 3px 12px rgba(0,0,0,.07); border-color: var(--border-md, rgba(0,0,0,.12)); }

.tbl-card-ico {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  background: rgba(27,58,107,.09); color: #1B3A6B;
  display: grid; place-items: center; font-size: 1rem;
}
.tbl-card-body { flex: 1; min-width: 0; }
.tbl-card-name { font-size: .83rem; font-weight: 700; color: var(--text, #0A0A0A); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tbl-card-sub  { font-size: .7rem; color: var(--text-3, #B4B4B4); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tbl-card-right { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: .3rem; }

/* ── pagination ── */
.tbl-pagination {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .7rem;
  padding: .8rem 1.15rem;
  border-top: 1px solid var(--border, rgba(0,0,0,.07));
  background: var(--bg, #fff);
}
.tbl-pag-info { font-size: .74rem; color: var(--text-3, #B4B4B4); font-family: var(--font-sans, -apple-system, sans-serif); }
.tbl-pag-info span { color: var(--text-2, #6B6B6B); font-weight: 600; }
.tbl-pag-right { display: flex; align-items: center; gap: .55rem; flex-wrap: wrap; }

.tbl-pag-size {
  display: inline-flex; align-items: center; gap: .32rem;
  font-size: .73rem; color: var(--text-2, #6B6B6B);
  font-family: var(--font-sans, -apple-system, sans-serif);
}
.tbl-pag-size select {
  padding: .24rem .48rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 7px; background: var(--bg, #fff);
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  font-size: .73rem; font-weight: 600; outline: none; cursor: pointer;
  transition: border-color .14s;
}
.tbl-pag-size select:focus { border-color: #1B3A6B; }

.tbl-pag-controls { display: flex; align-items: center; gap: .28rem; }
.tbl-pg-btn {
  width: 28px; height: 28px; border-radius: 7px;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg, #fff); color: var(--text-2, #6B6B6B);
  font-size: .77rem; font-weight: 600;
  display: grid; place-items: center; cursor: pointer;
  transition: all .13s;
  font-family: var(--font-sans, -apple-system, sans-serif);
}
.tbl-pg-btn:hover:not(:disabled) { border-color: #1B3A6B; color: #1B3A6B; background: rgba(27,58,107,.07); }
.tbl-pg-btn:disabled { opacity: .35; cursor: not-allowed; }
.tbl-pg-btn.active   { background: #1B3A6B; border-color: #1B3A6B; color: #fff; }
.tbl-pg-ellipsis { width: 28px; height: 28px; display: grid; place-items: center; font-size: .77rem; color: var(--text-3, #B4B4B4); }

/* ── responsive breakpoint ── */
@media(max-width: 640px) {
  .tbl-table-wrap { display: none; }
  .tbl-cards      { display: flex; }
  .tbl-pagination { justify-content: center; }
  .tbl-pag-info   { width: 100%; text-align: center; }
}
@media(max-width: 480px) {
  .tbl-toolbar { flex-direction: column; align-items: flex-start; }
}
`;
/* ── ICONS ── */
const IcoSearch = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IcoChevL = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>;
const IcoChevR = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>;
const IcoChevsL = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="11 17 6 12 11 7" /><polyline points="18 17 13 12 18 7" /></svg>;
const IcoChevsR = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></svg>;

/* ════════════════════════════════════════════════════════
   COMPONENT — lógica 100% original, nuevo shell visual
════════════════════════════════════════════════════════ */
const Table = ({
  table_name, table_data, table_headers, table_type,
  setShowModal, setEventHandler, setEventHandlerSettle, setSelectedMachineCode,
  setBuySelected, removeWebhook, setEditShowModal, setShowModalAsing,
  setShowSettleModal, setShowModalAsingUserRole, getUserDetails,
  removeUser, setSelectedMachine, setShowModalDetails, user,
  updateMachineStatus, getBranchofficesByNotification,
  getTableDetails, handleSwitchChange, generatePassword, generateTableQr, removeData,
  removeFromEnvironment, selectedEnvironment,
}) => {

  /* inject CSS once */
  React.useEffect(() => {
    const id = "tbl-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = TBL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  /* ════════════════════════════════════
     LÓGICA ORIGINAL — 100% INTACTA
  ════════════════════════════════════ */
  const [searchTerm, setSearchTerm] = useState('');

  const tableHeaders = () => {
    return table_headers.map((header, i) => <th key={i}>{header}</th>);
  };

  const filteredData = useMemo(() => {
    if (searchTerm === '') return table_data;
    return table_data.filter(data => {
      if (table_type === "general_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        return transactionMatch || dateMatch;
      } else if (table_type === "paymentmethod_report") {
        const transactionMatch = data.transaction_code.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        const paymentmethodMatch = data.paymenthod.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return transactionMatch || dateMatch || paymentmethodMatch;
      } else if (table_type === "products_report") {
        const productMatch = data.group_name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const totalAmountProductMatch = data.total_amount_selled.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return productMatch || totalAmountProductMatch;
      } else if (table_type === 'buy_table') {
        const invoiceMatch = data.invoice.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const supplierMatch = data.supplier_name === null ? '' : data.supplier_name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase());
        const dateMatch = data.createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        return invoiceMatch || supplierMatch || dateMatch;
      } else if (table_type === 'buy_sale_table') {
        const dateMatch = data.buy_sale_createdAt.toLowerCase().includes(searchTerm.toLowerCase());
        const productMatch = data.group_name.toLowerCase().includes(searchTerm.toLowerCase());
        return productMatch || dateMatch;
      } else if (table_type === 'integration_param_table') {
        return data.param_name.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (table_type === 'webhook_table') {
        return data.name.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (table_type === 'user_table') {
        console.log(data);
        const nameMatch = data.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const user_code = data.user_code.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const emailMatch = data.email.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const idMatch = data.idCard.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return nameMatch || user_code || idMatch || emailMatch;
      } else if (table_type === 'event_table') {
        const nameMatch = data.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const descriptionMatch = data.description.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return nameMatch || descriptionMatch;
      } else if (table_type === 'machine_table') {
        const nameMatch = data.machine_name !== null ? data.machine_name.toLowerCase().includes(searchTerm.toLocaleLowerCase()) : null;
        const codeMatch = data.code.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const machineType = data.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return nameMatch || codeMatch || machineType;
      } else if (table_type === "notification_types_table") {
        return data.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
      } else if (table_type === "rooms_table") {
        const nameMatch = data.name.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        const codeMatch = data.table_code.toLowerCase().includes(searchTerm.toLocaleLowerCase());
        return nameMatch || codeMatch;
      }
    });
  }, [searchTerm, table_data]);
  /* ════════════════════════════════════
     FIN LÓGICA ORIGINAL
  ════════════════════════════════════ */

  /* ── paginación UI ── */
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  /* FIX: reset a página 1 SOLO cuando cambia la búsqueda o el tamaño de página.
     Antes `table_data` estaba en las dependencias y cada acción (editar, borrar,
     switch, etc.) refrescaba los datos → referencia nueva → volvía a la página 1. */
  React.useEffect(() => { setCurrentPage(1); }, [searchTerm, pageSize]);

  /* FIX: si cambian los datos y la página actual dejó de existir
     (ej. borraste el último registro de la página 3), caer a la última válida. */
  React.useEffect(() => {
    const pages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    setCurrentPage(p => Math.min(p, pages));
  }, [filteredData.length, pageSize]);

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filteredData.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pageNumbers = (() => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (safePage <= 3) return [1, 2, 3, 4, '…', totalPages];
    if (safePage >= totalPages - 2) return [1, '…', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '…', safePage - 1, safePage, safePage + 1, '…', totalPages];
  })();

  /* ── tableContent — usa `paginated` en lugar de `filteredData` ── */
  const tableContent = () => {
    if (table_type === "general_report") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.createdAt.split('T')[0]}</td>
          <td>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td>{content.transaction_code}</td>
          <td>{'$ ' + (content.total === undefined ? '0.00' : content.total.toFixed(2))}</td>
          <td>{'$ ' + (content.tax === undefined ? '0.00' : content.tax.toFixed(2))}</td>
          <td>{'$ ' + (content.discount === undefined ? '0.00' : content.discount.toFixed(2))}</td>
        </tr>
      ));
    } else if (table_type === "paymentmethod_report") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.createdAt.split('T')[0]}</td>
          <td>{content.createdAt.split('T')[1].split(':')[0] + ':' + content.createdAt.split('T')[1].split(':')[1] + ' ' + (content.createdAt.split('T')[1].split(':')[0] <= 11 ? 'AM' : 'PM')}</td>
          <td>{content.transaction_code}</td>
          <td>{content.paymenthod}</td>
          <td>{'$ ' + (content.total === undefined ? '0.00' : content.total.toFixed(2))}</td>
        </tr>
      ));
    } else if (table_type === "products_report") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.group_name}</td>
          <td>{content.total_amount_selled === undefined ? '0' : content.total_amount_selled}</td>
          <td>{'$ ' + (content.total_selled === undefined ? '0.00' : content.total_selled.toFixed(2))}</td>
        </tr>
      ));
    } else if (table_type === "buy_table") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.createdAt.split('T')[0]}</td>
          <td>{content.is_void_buy === 1 ? 'Anulacion de compra' : 'Compra'}</td>
          <td>{content.supplier_name === null ? 'No registrado' : content.supplier_name}</td>
          <td>{content.invoice}</td>
          <td>{'$ ' + content.total.toFixed(2)}</td>
          <td>
            <div className="tbl-action-row">
              <button data-id={content.id} onClick={() => { setShowModal(true); setBuySelected(content.buy_id); setEventHandler(1); }} className="tbl-btn">
                <FaEye />
              </button>
            </div>
          </td>
        </tr>
      ));
    } else if (table_type === "buy_sale_table") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.buy_sale_createdAt.split('T')[0]}</td>
          <td>{content.group_name}</td>
          <td>{content.buy_sale_amount}</td>
          <td>{content.is_buy === 1 ? 'Compra' : 'Venta'}</td>
        </tr>
      ));
    } else if (table_type === 'integration_param_table') {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.param_name}</td>
          <td>{content.value}</td>
          <td>{content.description}</td>
        </tr>
      ));
    } else if (table_type === "webhook_table") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.name}</td>
          <td>{content.url}</td>
          <td>
            <div className="tbl-action-row">
              <button data-id={content.id} onClick={() => removeWebhook(content.id)} className="tbl-btn tbl-btn-danger">
                <FaRegTrashAlt />
              </button>
            </div>
          </td>
        </tr>
      ));
    } else if (table_type === 'user_table') {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td><img className="tbl-thumb" src={content.img} alt={content.name} /></td>
          <td>{content.name}</td>
          <td>{content.user_code}</td>
          <td>{content.idCard}</td>
          <td>{content.email}</td>
          <td>
            <div className="tbl-action-row">
              <button className="tbl-btn" role-id={content.role_id} user-id={content.user_id} onClick={() => { setEditShowModal(true); getUserDetails(content.user_id, "editModal"); }}>
                <i className="uil uil-pen" />
              </button>
              <button className="tbl-btn" role-id={content.role_id} user-id={content.user_id} onClick={() => { setShowModalAsing(true); getUserDetails(content.user_id, 'asingModal'); }}>
                <i className="uil uil-list-ol" />
              </button>
              <button className="tbl-btn" role-id={content.role_id} user-id={content.user_id} onClick={() => { setShowModalAsingUserRole(true); getUserDetails(content.user_id, 'asingModal'); }}>
                <i className="uil uil-list-ol" />
              </button>
              <button className="tbl-btn tbl-btn-danger" role-id={content.role_id} user-id={content.user_id} onClick={() => removeUser(content.user_id)}>
                <i className="uil uil-trash" />
              </button>
            </div>
          </td>
        </tr>
      ));
    } else if (table_type === 'event_table') {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>{content.initialDate.split('T')[0]}</td>
          <td>{content.maxDate.split('T')[0]}</td>
          <td>{content.public}</td>
        </tr>
      ));
    } else if (table_type === 'machine_table') {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>
            {content.is_logged
              ? <span className="tbl-badge tbl-badge-success">Activa</span>
              : <span className="tbl-badge tbl-badge-danger">Inactiva</span>}
          </td>
          <td>{content.machine_name !== null ? content.machine_name : 'No registrado'}</td>
          <td>{content.name}</td>
          <td>{content.code}</td>
          <td>
            <div className="tbl-action-row">
              <RenderIf isTrue={checkUserPermission("get_details_machine", user.permissions) !== -1}>
                <button className="tbl-btn" onClick={() => { setSelectedMachine(content.machine_per_branch_id); setEventHandler(1); setShowModalDetails(true); }}>
                  <i className="uil uil-eye" />
                </button>
              </RenderIf>
              <RenderIf isTrue={checkUserPermission("update_login_machine_status", user.permissions) !== -1}>
                <button className="tbl-btn" onClick={() => { setEventHandler(1); updateMachineStatus(content.code, false); }}>
                  <i className="uil uil-sync" />
                </button>
              </RenderIf>
              <button className="tbl-btn" onClick={() => { setEventHandlerSettle(1); setSelectedMachine(content.machine_per_branch_id); setSelectedMachineCode(content.code); setShowSettleModal(true); }}>
                <i className="uil uil-coins" />
              </button>
            </div>
          </td>
        </tr>
      ));
    } else if (table_type === "notification_types_table") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td><img className="tbl-thumb" src={content.secure_url === null ? defaultImg : content.secure_url} alt={content.name} /></td>
          <td>{content.name}</td>
          <td>{content.description}</td>
          <td>
            <div className="tbl-action-row">
              <button className="tbl-btn" onClick={() => { setShowModal(true); getBranchofficesByNotification(content); }}>
                <i className="uil uil-eye" />
              </button>
              <button className="tbl-btn" onClick={() => { setEditShowModal(true); getBranchofficesByNotification(content); }}>
                <i className="uil uil-pen" />
              </button>
            </div>
          </td>
        </tr>
      ));
    } else if (table_type === "rooms_table") {
      return paginated.map((content, index) => (
        <tr key={index}>
          <td>
            <div className="tbl-switch">
              <Form.Switch type="switch" id={content.id} label="" checked={content.status === 1} onChange={handleSwitchChange} />
            </div>
          </td>
          <td>
            {content.status === 1
              ? <span className="tbl-badge tbl-badge-success">Activa</span>
              : <span className="tbl-badge tbl-badge-danger">Inactiva</span>}
          </td>
          <td style={{ fontWeight: 600, color: 'var(--text, #0A0A0A)' }}>{content.name}</td>
          <td>{content.table_code}</td>
          <td style={{ fontFamily: 'monospace', fontSize: '.78rem' }}>{content.password}</td>
          <td>{content.client_name !== null ? content.client_name : 'No asignado'}</td>
          <td>
            <div className="tbl-action-row">
              <button className="tbl-btn" title="Generar contraseña" data-id={content.id} onClick={generatePassword}>
                <i className="uil uil-key-skeleton-alt" />
              </button>
              <button className="tbl-btn" title="Generar QR / URL NFC" onClick={() => generateTableQr(content)}>
                <i className="uil uil-qrcode-scan" />
              </button>
              <button className="tbl-btn" title="Editar" onClick={() => { setEditShowModal(true); getTableDetails(content.id, "edit"); }}>
                <i className="uil uil-pen" />
              </button>
              <button className="tbl-btn tbl-btn-danger" title="Eliminar" data-id={content.id} onClick={removeData}>
                <i className="uil uil-trash" />
              </button>
            </div>
          </td>
        </tr>
      ));
    }
  };

  /* ── helper: card subtitle by type ── */
  const cardSub = (row) => {
    if (table_type === 'general_report') return row.transaction_code;
    if (table_type === 'paymentmethod_report') return row.paymenthod;
    if (table_type === 'products_report') return `Vendidos: ${row.total_amount_selled}`;
    if (table_type === 'buy_table') return `Factura: ${row.invoice}`;
    if (table_type === 'user_table') return row.email;
    if (table_type === 'machine_table') return `Código: ${row.code}`;
    if (table_type === 'rooms_table') return `#${row.table_code}`;
    if (table_type === 'webhook_table') return row.url;
    return '';
  };
  const cardName = (row) => {
    if (table_type === 'general_report' || table_type === 'paymentmethod_report') return row.transaction_code;
    if (table_type === 'products_report') return row.group_name;
    if (table_type === 'buy_table') return row.supplier_name || 'Sin proveedor';
    if (table_type === 'user_table') return row.name;
    if (table_type === 'machine_table') return row.machine_name || row.name;
    if (table_type === 'rooms_table') return row.name;
    if (table_type === 'webhook_table') return row.name;
    if (table_type === 'notification_types_table') return row.name;
    return '';
  };

  /* ── RENDER ── */
  return (
    <div className="tbl-wrap">

      {/* ── TOOLBAR ── */}
      <div className="tbl-toolbar">
        <span className="tbl-title">
          {table_name}
          <span className="tbl-count">{totalItems}</span>
        </span>
        <div className="tbl-search">
          <IcoSearch />
          <input
            type="text"
            placeholder="Buscar..."
            spellCheck="false"
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── EMPTY ── */}
      {totalItems === 0 && (
        <div className="tbl-empty">
          <div className="tbl-empty-icon">📋</div>
          <p className="tbl-empty-title">Sin resultados</p>
          <p className="tbl-empty-sub">{searchTerm ? `No hay coincidencias para "${searchTerm}".` : 'No hay datos disponibles.'}</p>
        </div>
      )}

      {/* ── TABLA DESKTOP ── */}
      {totalItems > 0 && (
        <div className="tbl-table-wrap">
          <table className="tbl-table">
            <thead><tr>{tableHeaders()}</tr></thead>
            <tbody>{tableContent()}</tbody>
          </table>
        </div>
      )}

      {/* ── CARDS MOBILE ── */}
      {totalItems > 0 && (
        <div className="tbl-cards">
          {paginated.map((row, i) => (
            <div className="tbl-card" key={i} style={{ animationDelay: `${i * 0.04}s` }}>
              {/* thumbnail si tiene img */}
              {(table_type === 'user_table' || table_type === 'notification_types_table') && (
                <img
                  className="tbl-thumb"
                  src={table_type === 'user_table' ? row.img : (row.secure_url || defaultImg)}
                  alt={cardName(row)}
                  style={{ width: 38, height: 38, borderRadius: 8 }}
                />
              )}

              {/* ícono default */}
              {table_type !== 'user_table' && table_type !== 'notification_types_table' && (
                <div className="tbl-card-ico">
                  {table_type === 'rooms_table'
                    ? <i className="uil uil-bed" />
                    : table_type === 'machine_table'
                      ? <i className="uil uil-desktop" />
                      : <i className="uil uil-file-alt" />}
                </div>
              )}

              <div className="tbl-card-body">
                <div className="tbl-card-name">{cardName(row)}</div>
                <div className="tbl-card-sub">{cardSub(row)}</div>
              </div>

              <div className="tbl-card-right">
                {/* badge estado */}
                {table_type === 'rooms_table' && (
                  <span className={`tbl-badge ${row.status === 1 ? 'tbl-badge-success' : 'tbl-badge-danger'}`}>
                    {row.status === 1 ? 'Activa' : 'Inactiva'}
                  </span>
                )}
                {table_type === 'machine_table' && (
                  <span className={`tbl-badge ${row.is_logged ? 'tbl-badge-success' : 'tbl-badge-danger'}`}>
                    {row.is_logged ? 'Activa' : 'Inactiva'}
                  </span>
                )}
                {/* precio */}
                {(table_type === 'general_report' || table_type === 'paymentmethod_report' || table_type === 'buy_table') && (
                  <span style={{ fontWeight: 700, color: '#1B3A6B', fontSize: '.82rem' }}>
                    {'$ ' + (row.total?.toFixed(2) ?? '0.00')}
                  </span>
                )}

                {/* acciones compactas */}
                <div className="tbl-action-row">
                  {table_type === 'rooms_table' && (
                    <>
                      <button className="tbl-btn" title="Editar" onClick={() => { setEditShowModal(true); getTableDetails(row.id, "edit"); }}>
                        <i className="uil uil-pen" />
                      </button>
                      <button className="tbl-btn tbl-btn-danger" title="Eliminar" data-id={row.id} onClick={removeData}>
                        <i className="uil uil-trash" />
                      </button>
                    </>
                  )}
                  {table_type === 'user_table' && (
                    <>
                      <button className="tbl-btn" onClick={() => { setEditShowModal(true); getUserDetails(row.user_id, "editModal"); }}>
                        <i className="uil uil-pen" />
                      </button>
                      <button className="tbl-btn tbl-btn-danger" onClick={() => removeUser(row.user_id)}>
                        <i className="uil uil-trash" />
                      </button>
                    </>
                  )}
                  {table_type === 'buy_table' && (
                    <button className="tbl-btn" onClick={() => { setShowModal(true); setBuySelected(row.buy_id); setEventHandler(1); }}>
                      <FaEye />
                    </button>
                  )}
                  {table_type === 'webhook_table' && (
                    <button className="tbl-btn tbl-btn-danger" onClick={() => removeWebhook(row.id)}>
                      <FaRegTrashAlt />
                    </button>
                  )}
                  {table_type === 'notification_types_table' && (
                    <>
                      <button className="tbl-btn" onClick={() => { setShowModal(true); getBranchofficesByNotification(row); }}>
                        <i className="uil uil-eye" />
                      </button>
                      <button className="tbl-btn" onClick={() => { setEditShowModal(true); getBranchofficesByNotification(row); }}>
                        <i className="uil uil-pen" />
                      </button>
                    </>
                  )}
                  {table_type === 'machine_table' && (
                    <button className="tbl-btn" onClick={() => { setSelectedMachine(row.machine_per_branch_id); setEventHandler(1); setShowModalDetails(true); }}>
                      <i className="uil uil-eye" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── PAGINACIÓN ── */}
      {totalItems > 0 && (
        <div className="tbl-pagination">
          <div className="tbl-pag-info">
            Mostrando <span>{(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, totalItems)}</span> de <span>{totalItems}</span>
          </div>
          <div className="tbl-pag-right">
            <div className="tbl-pag-size">
              Mostrar
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
              por página
            </div>
            <div className="tbl-pag-controls">
              <button className="tbl-pg-btn" onClick={() => setCurrentPage(1)} disabled={safePage === 1}><IcoChevsL /></button>
              <button className="tbl-pg-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}><IcoChevL /></button>
              {pageNumbers.map((pg, i) =>
                pg === '…'
                  ? <span className="tbl-pg-ellipsis" key={`e${i}`}>…</span>
                  : <button key={pg} className={`tbl-pg-btn${safePage === pg ? ' active' : ''}`} onClick={() => setCurrentPage(pg)}>{pg}</button>
              )}
              <button className="tbl-pg-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}><IcoChevR /></button>
              <button className="tbl-pg-btn" onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}><IcoChevsR /></button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Table;